import { useState } from "react";
import { Mail, Phone, MapPin, Globe, Printer, Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { BlobShape, LeafBranch } from "../components/Organic";
import { submitContact } from "../lib/api";
import { BRAND, THERAPY_FOR_OPTIONS } from "../lib/content";

const initial = {
  name: "",
  email: "",
  phone: "",
  therapy_for: "adult",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email.";
    if (!form.message.trim() || form.message.trim().length < 5)
      e.message = "Tell me a little about what's bringing you here.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) =>
    setForm((f) => ({ ...f, [k]: ev.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact({
        ...form,
        phone: form.phone || null,
      });
      setSubmitted(true);
      setForm(initial);
      toast.success("Thank you — I'll be in touch within 1 business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="relative">
      <Header />
      <section className="relative pb-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Sidebar />
          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-[2.5rem] bg-[var(--at-bg)] border border-[var(--at-border)] p-7 md:p-10 shadow-[0_30px_60px_-30px_rgba(26,46,68,0.15)]">
                {submitted ? (
                  <SuccessState onReset={() => setSubmitted(false)} />
                ) : (
                  <form
                    data-testid="contact-form"
                    onSubmit={onSubmit}
                    className="space-y-6"
                    noValidate
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field
                        label="Full Name"
                        error={errors.name}
                        required
                      >
                        <input
                          data-testid="contact-name"
                          type="text"
                          value={form.name}
                          onChange={onChange("name")}
                          placeholder="Jane Doe"
                          className="form-input"
                          autoComplete="name"
                        />
                      </Field>
                      <Field
                        label="Email Address"
                        error={errors.email}
                        required
                      >
                        <input
                          data-testid="contact-email"
                          type="email"
                          value={form.email}
                          onChange={onChange("email")}
                          placeholder="you@example.com"
                          className="form-input"
                          autoComplete="email"
                        />
                      </Field>
                    </div>
                    <Field label="Phone Number (optional)">
                      <input
                        data-testid="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="(555) 123-4567"
                        className="form-input"
                        autoComplete="tel"
                      />
                    </Field>

                    <fieldset>
                      <legend className="block text-sm font-semibold text-[var(--at-navy)] mb-3">
                        Who is this therapy for?
                      </legend>
                      <div
                        data-testid="contact-therapy-for"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                      >
                        {THERAPY_FOR_OPTIONS.map((opt) => {
                          const checked = form.therapy_for === opt.value;
                          return (
                            <label
                              key={opt.value}
                              data-testid={`therapy-for-${opt.value}`}
                              data-state={checked ? "checked" : "unchecked"}
                              className="radio-card"
                            >
                              <input
                                type="radio"
                                name="therapy_for"
                                value={opt.value}
                                checked={checked}
                                onChange={onChange("therapy_for")}
                                className="sr-only"
                              />
                              <span
                                className={`w-4 h-4 rounded-full border-2 transition-colors ${
                                  checked
                                    ? "border-[var(--at-teal)] bg-[var(--at-teal)]"
                                    : "border-[var(--at-border-strong)]"
                                }`}
                              />
                              <span className="text-sm font-medium text-[var(--at-navy)]">
                                {opt.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    <Field
                      label="How can I help you?"
                      error={errors.message}
                      required
                    >
                      <textarea
                        data-testid="contact-message"
                        rows={5}
                        value={form.message}
                        onChange={onChange("message")}
                        placeholder="Share what's been on your mind. There's no wrong way to start."
                        className="form-input resize-none"
                      />
                    </Field>

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Submit Request <Send size={16} />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-[var(--at-muted)] text-center leading-relaxed">
                      Your information stays private. I'll only use it to
                      respond to your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <style>{`
        .form-input {
          width: 100%;
          background: var(--at-bg-2);
          border: 1.5px solid transparent;
          border-radius: 1rem;
          padding: 0.9rem 1.1rem;
          color: var(--at-navy);
          font-family: 'Figtree', sans-serif;
          font-size: 1rem;
          transition: border-color 0.2s, background 0.2s;
        }
        .form-input::placeholder { color: var(--at-muted); }
        .form-input:focus {
          outline: none;
          border-color: var(--at-teal);
          background: var(--at-bg);
        }
      `}</style>
    </div>
  );
}

function Header() {
  return (
    <section className="relative pt-44 md:pt-52 pb-14 md:pb-16 px-6 md:px-10 overflow-hidden">
      <BlobShape
        className="absolute -top-24 -left-32 w-[520px] h-[520px]"
        color="#8DAA91"
        opacity={0.22}
      />
      <BlobShape
        className="absolute -bottom-32 -right-32 w-[440px] h-[440px]"
        color="#E4BE9E"
        opacity={0.25}
      />
      <div className="relative max-w-5xl mx-auto text-center">
        <Reveal>
          <span className="overline">Contact / Next Steps</span>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="h-display mt-5 text-5xl md:text-6xl lg:text-7xl">
            Let's build a path forward,{" "}
            <span className="h-italic-accent">together.</span>
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-7 text-lg md:text-xl text-[var(--at-navy-soft)] max-w-3xl mx-auto leading-relaxed">
            Taking the first step toward therapy can feel daunting — but you
            don't have to walk it alone. Whether you're searching for yourself,
            your child, or your family,{" "}
            <span className="font-semibold text-[var(--at-navy)]">
              All Thrive Therapy and Consulting, PLLC
            </span>{" "}
            is here to hold space for you.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Sidebar() {
  const steps = [
    {
      n: "01",
      title: "Reach Out",
      body: "Fill out the brief contact form. No commitment — just a hello.",
    },
    {
      n: "02",
      title: "Connect",
      body:
        "I'll schedule a free initial consultation to discuss your needs and make sure I'm a good fit.",
    },
    {
      n: "03",
      title: "Grow",
      body:
        "Together we'll build a personalized plan that honors your goals and leverages my partner network.",
    },
  ];
  return (
    <div className="lg:col-span-5 lg:sticky lg:top-32 space-y-8">
      <Reveal>
        <div className="rounded-[2rem] bg-[var(--at-navy)] text-[var(--at-bg)] p-8 md:p-10 relative overflow-hidden">
          <LeafBranch
            className="absolute -top-6 -right-6 w-32 h-32"
            color="#8DAA91"
            opacity={0.4}
          />
          <h3 className="font-serif text-2xl leading-snug">How to get started</h3>
          <ol className="mt-6 space-y-5">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-4">
                <span className="font-serif text-3xl text-[var(--at-olive)] shrink-0 leading-none">
                  {s.n}
                </span>
                <div>
                  <p className="font-semibold text-[var(--at-bg)]">{s.title}</p>
                  <p className="text-sm text-[var(--at-bg)]/75 mt-1 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Reveal>

      <Reveal delay={1}>
        <div className="rounded-[2rem] bg-[var(--at-bg-2)] border border-[var(--at-border)] p-8 space-y-5">
          <p className="overline">Reach me directly</p>
          <ContactItem
            icon={Mail}
            label="Email"
            value={BRAND.email}
            href={`mailto:${BRAND.email}`}
            testid="sidebar-email"
          />
          <ContactItem
            icon={Phone}
            label="Phone"
            value={BRAND.phone}
            href={`tel:${BRAND.phoneHref}`}
            testid="sidebar-phone"
          />
          <ContactItem
            icon={Printer}
            label="Fax"
            value={BRAND.fax}
            testid="sidebar-fax"
          />
          <ContactItem
            icon={MapPin}
            label="Address"
            value={BRAND.address}
            testid="sidebar-address"
          />
          <ContactItem
            icon={Globe}
            label="Website"
            value={BRAND.website}
            href={BRAND.websiteHref}
            testid="sidebar-website"
          />
        </div>
      </Reveal>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-[var(--at-bg)] border border-[var(--at-border)] flex items-center justify-center text-[var(--at-teal)] shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[var(--at-muted)] font-semibold">
          {label}
        </p>
        <p className="text-[var(--at-navy)] font-medium">{value}</p>
      </div>
    </div>
  );
  if (href)
    return (
      <a href={href} data-testid={testid} className="block">
        {inner}
      </a>
    );
  return <div data-testid={testid}>{inner}</div>;
}

function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[var(--at-navy)] mb-2">
        {label}
        {required && <span className="text-[var(--at-teal)] ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p data-testid="field-error" className="mt-1.5 text-xs text-red-600 font-medium">
          {error}
        </p>
      )}
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div data-testid="contact-success" className="text-center py-8">
      <div className="w-16 h-16 mx-auto rounded-full bg-[var(--at-teal-tint)] flex items-center justify-center text-[var(--at-teal)]">
        <CheckCircle2 size={32} />
      </div>
      <h3 className="mt-6 font-serif text-3xl text-[var(--at-navy)]">
        Message received.
      </h3>
      <p className="mt-3 text-[var(--at-navy-soft)] max-w-md mx-auto leading-relaxed">
        Thank you for reaching out. I'll be in touch within one business day
        to schedule your free consultation.
      </p>
      <button
        onClick={onReset}
        data-testid="contact-success-reset"
        className="btn-outline mt-8"
      >
        Send another message
      </button>
    </div>
  );
}
